/**
 * Composable per la gestione della connessione WebSocket
 * Fornisce un'interfaccia semplice per sottoscrivere e ascoltare eventi real-time
 */

import { ref, onUnmounted, watch } from 'vue';
import { io } from 'socket.io-client';

// Istanza singleton del socket
let socketInstance = null;
const isConnected = ref(false);
// Tracks how many active consumers have joined each room.
// leaveRoom only emits to the server when the last consumer leaves.
const roomRefCounts = new Map();
const roomTokens = new Map();

// Le room conversation: richiedono il token della conversazione (card #10434653609).
const joinPayload = (roomId) => {
  const token = roomTokens.get(roomId);
  return token ? { room: roomId, token } : roomId;
};

// Logged-once-per-outage guard: reconnection is automatic and unlimited, so we
// log only the first connect_error of an outage instead of one per attempt.
let connectErrorLogged = false;
// Attach the global 'online' listener only once across the singleton's lifetime.
let onlineListenerAttached = false;

/**
 * Hook per utilizzare WebSocket nell'applicazione
 */
export function useWebSocket() {
  /**
   * Inizializza la connessione WebSocket
   */
  const connect = () => {
    // Reuse the existing singleton even while it is (re)connecting. socket.io
    // reconnects the SAME instance automatically; recreating it here would orphan
    // the old socket along with every listener attached via on() (e.g. the
    // 'message:incoming' handler), silently killing real-time delivery until reload.
    if (socketInstance) {
      return socketInstance;
    }

    // Use same-origin socket connection so cookies are sent by the browser.
    // The Vite dev server proxies `/socket.io` to the backend (ws: true).
    const newSocket = io(undefined, {
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      // Never give up: with a finite cap (was 5) the socket stayed permanently
      // dead after a short outage, silently killing all real-time updates until a
      // page reload. Infinity keeps retrying (every ~5s) and self-heals.
      reconnectionAttempts: Infinity,
      withCredentials: true,  // Required to send Clerk cookies for authentication
      auth: async (cb) => {
        // Try to get Clerk session token
        try {
          if (window.Clerk) {
            if (typeof window.Clerk.load === 'function') {
              // 3-second timeout prevents socket from hanging if Clerk CDN is unreachable
              await Promise.race([
                window.Clerk.load(),
                new Promise((_, reject) => setTimeout(() => reject(new Error('Clerk.load timeout')), 3000)),
              ]);
            }
            const token = await window.Clerk.session?.getToken();
            if (token) {
              cb(null, { token });
              return;
            }
          }
        } catch (e) {
          Console.warn('[WebSocket] Failed to get Clerk token:', e);
        }
        cb(null, { token: null }); // Continue without token
      }
    });

    socketInstance = newSocket;

    // When the browser regains connectivity (laptop wakes from sleep, ISP drops
    // and recovers), nudge an immediate reconnect instead of waiting out the
    // backoff timer — socket.io's reconnection loop can stall while a background
    // tab's timers are throttled.
    if (!onlineListenerAttached) {
      onlineListenerAttached = true;
      window.addEventListener('online', () => {
        if (socketInstance && !socketInstance.connected) {
          socketInstance.connect();
        }
      });
    }

    newSocket.on('connect', () => {
      Console.log('✅ WebSocket connected:', newSocket.id);
      isConnected.value = true;
      connectErrorLogged = false; // reset the once-per-outage error log guard
      // Re-join all active rooms after (re)connect
      for (const roomId of roomRefCounts.keys()) {
        newSocket.emit('join-room', joinPayload(roomId));
      }
    });

    newSocket.on('disconnect', (reason) => {
      Console.log('❌ WebSocket disconnected:', reason);
      isConnected.value = false;
    });

    newSocket.on('connect_error', (error) => {
      isConnected.value = false;
      // Log only the first failure of an outage; the unlimited reconnection loop
      // would otherwise emit one error per attempt and flood the console. The
      // guard resets on a successful (re)connect.
      if (!connectErrorLogged) {
        Console.error('WebSocket connection error:', error);
        connectErrorLogged = true;
      }
    });

    return newSocket;
  };

  /**
   * Disconnette il WebSocket
   */
  const disconnect = () => {
    if (socketInstance) {
      socketInstance.disconnect();
      socketInstance = null;
      isConnected.value = false;
    }
  };

  /**
   * Join a una room specifica
   * @param {string} roomId - ID della room
   * @param {string} [token] - token della conversazione (room conversation)
   */
  const joinRoom = (roomId, token) => {
    roomRefCounts.set(roomId, (roomRefCounts.get(roomId) || 0) + 1);
    if (token) roomTokens.set(roomId, token);
    if (socketInstance?.connected) {
      socketInstance.emit('join-room', joinPayload(roomId));
      Console.log(`Joined room: ${roomId} (refs: ${roomRefCounts.get(roomId)})`);
    }
  };

  /**
   * Leave da una room specifica
   * @param {string} roomId - ID della room
   */
  const leaveRoom = (roomId) => {
    const count = roomRefCounts.get(roomId) || 0;
    if (count <= 1) {
      roomRefCounts.delete(roomId);
      roomTokens.delete(roomId);
      if (socketInstance?.connected) {
        socketInstance.emit('leave-room', roomId);
        Console.log(`Left room: ${roomId}`);
      }
    } else {
      roomRefCounts.set(roomId, count - 1);
      Console.log(`Decremented room ref: ${roomId} (refs: ${count - 1})`);
    }
  };

  /**
   * Sottoscrive un evento
   * @param {string} event - Nome dell'evento
   * @param {Function} callback - Funzione da chiamare quando l'evento viene ricevuto
   * @returns {Function} Funzione per rimuovere il listener
   */
  const on = (event, callback) => {
    if (!socketInstance) {
      connect();
    }
    
    socketInstance.on(event, callback);
    
    // Ritorna una funzione per rimuovere il listener
    return () => {
      if (socketInstance) {
        socketInstance.off(event, callback);
      }
    };
  };

  /**
   * Rimuove un listener per un evento
   * @param {string} event - Nome dell'evento
   * @param {Function} callback - Callback da rimuovere
   */
  const off = (event, callback) => {
    if (socketInstance) {
      socketInstance.off(event, callback);
    }
  };

  /**
   * Emette un evento al server
   * @param {string} event - Nome dell'evento
   * @param {any} data - Dati da inviare
   */
  const emit = (event, data) => {
    if (socketInstance && socketInstance.connected) {
      socketInstance.emit(event, data);
    } else {
      Console.warn(`Cannot emit event "${event}": socket not connected`);
    }
  };

  return {
    isConnected,
    connect,
    disconnect,
    joinRoom,
    leaveRoom,
    on,
    off,
    emit
  };
}

/**
 * Hook specifico per ascoltare gli eventi di cambio stato dei documenti
 * @param {string} botId - ID del bot
 * @param {Function} onStatusChange - Callback chiamato quando lo stato cambia
 */
export function useDocumentEvents(botId, onStatusChange) {
  const { connect, joinRoom, leaveRoom, on } = useWebSocket();
  
  // Connetti al socket
  const socket = connect();
  const roomId = `bot:${botId}`;
  
  // Funzione per fare il join della room
  const doJoin = () => {
    joinRoom(roomId);
  };
  
  // Se il socket è già connesso, fai subito il join
  if (socket.connected) {
    doJoin();
  } else {
    // Altrimenti aspetta l'evento di connessione
    socket.once('connect', () => {
      doJoin();
    });
  }

  // Sottoscrive l'evento di cambio stato
  const unsubscribe = on('document:status-change', (data) => {
    if (data.botId === botId && onStatusChange) {
      onStatusChange(data.document);
    }
  });

  // Cleanup quando il componente viene smontato
  onUnmounted(() => {
    unsubscribe();
    leaveRoom(roomId);
  });

  return {
    unsubscribe
  };
}

/**
 * Hook specifico per ascoltare gli eventi di aggiornamento token dell'utente
 * @param {Function} onTokenUpdate - Callback chiamato quando i token vengono aggiornati
 */
export function useTokenEvents(onTokenUpdate) {
  const { connect, on } = useWebSocket();
  
  // Connetti al socket (l'utente viene automaticamente joinato alla sua room user:{userId})
  connect();

  // Sottoscrive l'evento di aggiornamento token
  const unsubscribe = on('tokens:updated', (data) => {
    if (onTokenUpdate) {
      onTokenUpdate(data);
    }
  });

  // Cleanup quando il componente viene smontato
  onUnmounted(() => {
    unsubscribe();
  });

  return {
    unsubscribe
  };
}

/**
 * Hook specifico per ascoltare gli eventi di aggiornamento conversazione
 * @param {Function} onConversationUpdate - Callback chiamato quando una conversazione viene aggiornata
 */
export function useConversationEvents(onConversationUpdate) {
  const { connect, on } = useWebSocket();

  // Connetti al socket (l'utente viene automaticamente joinato alla sua room user:{userId})
  connect();

  // Sottoscrive l'evento di aggiornamento conversazione
  const unsubscribe = on('conversation:updated', (data) => {
    if (onConversationUpdate) {
      onConversationUpdate(data);
    }
  });

  // Cleanup quando il componente viene smontato
  onUnmounted(() => {
    unsubscribe();
  });

  return {
    unsubscribe
  };
}

/**
 * Joins the conversation socket room and listens for messages pushed from webhook flows.
 * @param {import('vue').Ref<string|null>} conversationIdRef - Reactive ref of the conversation ID
 * @param {Function} onMessage - Called with { content, type, source, timestamp }
 * @param {Function} [onTyping] - Optional. Called with a boolean when the agent starts/stops
 *   "typing" (event `agent:typing`, emitted around triggerAgent responses). Reuses the same
 *   room membership as onMessage — no extra join/leave.
 * @param {import('vue').Ref<string|null>} [conversationTokenRef] - Conversation token, sent with the room join
 */
export function useIncomingMessages(conversationIdRef, onMessage, onTyping, conversationTokenRef) {
  const { connect, joinRoom, leaveRoom, on } = useWebSocket();

  let currentRoom = null;

  function joinConversation(id) {
    // Always leave the previous room first — even when switching to no conversation
    // (id null on reset). The old early-return left the socket subscribed to the
    // previous conversation's room ("stale room"), so messages for the new
    // conversation were missed until the next response forced a re-join.
    if (currentRoom) {
      leaveRoom(currentRoom);
      currentRoom = null;
    }
    if (!id) return;
    currentRoom = `conversation:${id}`;
    const socket = connect();
    const doJoin = () => joinRoom(currentRoom, conversationTokenRef?.value);
    if (socket.connected) {
      doJoin();
    } else {
      socket.once('connect', doJoin);
    }
  }

  const handler = (data) => { if (onMessage) onMessage(data); };

  const unsubscribe = on('message:incoming', handler);

  // Optional: agent "typing" toggle emitted around triggerAgent responses.
  let unsubscribeTyping = null;
  if (onTyping) {
    const typingHandler = (data) => onTyping(!!(data && data.isTyping));
    unsubscribeTyping = on('agent:typing', typingHandler);
  }

  watch(conversationIdRef, (id) => {
    joinConversation(id);
  }, { immediate: true });

  onUnmounted(() => {
    unsubscribe();
    if (unsubscribeTyping) unsubscribeTyping();
    if (currentRoom) leaveRoom(currentRoom);
  });
}
